import React, { useState, useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Box
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Iconify from 'src/components/iconify';
import CustomFilter from 'src/components/filter/CustomFilter';
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import Services from 'src/services/Services';
import moment from 'moment';
import { toast } from 'react-toastify';

export default function ArasReportList({ parsedUser, tabs, onFilteredDataChange, externalDateRange }) {
    const [reportList, setReportList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({});
    const [showFilterInput, setShowFilterInput] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDetail, setOpenDetail] = useState(false);
    const [formData, setFormData] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');

    const TABLE_HEAD = [
        { id: 'inBranchDate', label: "Kargo Geliş Tarihi", format: (value) => formatDate(value) },
        { id: 'barcode', label: "Gönderi No" },
        { id: 'customerName', label: "Müşteri Ad Soyad" },
        { id: 'staff', label: "EP Personel Ad Soyadı" },
        { id: 'description', label: "Açıklama", format: (value) => value ? value : "-" },
        { id: 'processingType', label: "İşlem Türü" },
        { id: 'price', label: "Tutar", format: (value) => value ? value : "-" },
        { id: 'paymentType', label: "Ödeme Türü" },
        { id: 'processingDetails', label: "İşlem Detay" },
        { id: 'poiTitle', label: "Lokasyon" },
        {
            id: 'action',
            label: '',
            alignRight: false,
            disableFilter: true,
            format: (value, row) => (
                <Tooltip title={"Detay"}>
                    <Iconify
                        style={{ color: '#98A2B3', width: 22, height: 22, cursor: 'pointer' }}
                        icon="bx:detail"
                        onClick={() => handleOpenDetail(row)}
                    />
                </Tooltip>
            ),
        }
    ];

    const formatDate = (timestampOrMoment) => {
        if (moment.isMoment(timestampOrMoment)) return timestampOrMoment.format("DD.MM.YYYY");
        if (!timestampOrMoment) return "-";
        const date = new Date(timestampOrMoment * 1000);
        const y = date.getFullYear();
        if (isNaN(y) || y === 1970) return "-";
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${d}.${m}.${y}`;
    };

    const uniqueValues = (key) =>
        Array.from(new Set((originalData || []).map(x => x?.[key]).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), "tr-TR"));

    const applyFilters = (data, currentFilters) => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) return data;

        const asJsDate = (val, type = "start") => {
            if (!val) return null;
            if (moment.isMoment(val)) return (type === "start" ? val.startOf('day') : val.endOf('day')).toDate();
            if (val instanceof Date) return val;
            return new Date(val);
        };

        return (data || []).filter((item) => {
            return Object.entries(currentFilters).every(([key, filterValue]) => {
                if (!filterValue) return true;

                if (key === 'inBranchDate' && typeof filterValue === 'object' && (filterValue.start || filterValue.end)) {
                    const itemDate = item[key] ? new Date(item[key] * 1000) : null;
                    const start = asJsDate(filterValue?.start, 'start');
                    const end = asJsDate(filterValue?.end, 'end');
                    if ((start || end) && !itemDate) return false;
                    if (start && itemDate && itemDate < start) return false;
                    if (end && itemDate && itemDate > end) return false;
                    return true;
                }

                if (Array.isArray(filterValue)) {
                    if (!filterValue.length) return true;
                    const raw = item[key];
                    const val = typeof raw === 'string' ? raw : String(raw ?? '');
                    return filterValue.map(String).includes(val);
                }

                if (typeof filterValue === 'string') {
                    if (filterValue.trim() === '') return true;
                    
                    let itemValue = item[key];
                    const columnDef = TABLE_HEAD.find(col => col.id === key);
                    
                    if (columnDef?.format && typeof columnDef.format === 'function' && key !== 'action') {
                        try { 
                            const formatted = columnDef.format(itemValue, item); 
                            if (typeof formatted === 'string' || typeof formatted === 'number') {
                                itemValue = formatted;
                            }
                        } catch { }
                    }

                    return (
                        String(itemValue || '').toLocaleUpperCase('tr-TR')
                            .includes(filterValue.toLocaleUpperCase('tr-TR'))
                    );
                }
                
                return true;
            });
        });
    };

    const applyExternalDateRange = (data) => {
        const startDate = externalDateRange?.startDate ? new Date(`${externalDateRange.startDate}T00:00:00`) : null;
        const endDate = externalDateRange?.endDate ? new Date(`${externalDateRange.endDate}T23:59:59`) : null;

        if (!startDate && !endDate) {
            return data;
        }

        return (data || []).filter((item) => {
            const itemDate = item?.inBranchDate ? new Date(item.inBranchDate * 1000) : null;
            if (!itemDate) return false;
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;
            return true;
        });
    };

    useEffect(() => {
        getReports();
    }, []);

    useEffect(() => {
        const filtered = applyExternalDateRange(applyFilters(originalData, filters));
        setReportList(filtered);
        setPage(0); 
    }, [filters, originalData, externalDateRange]);

    useEffect(() => {
        onFilteredDataChange?.(reportList);
    }, [reportList, onFilteredDataChange]);

    const getReports = () => {
        setLoading(true);
        Services.GetOperationReport(parsedUser.token, "aras-kargo", externalDateRange.startDate, externalDateRange.endDate)
            .then((res) => {
                if (res.data.status) {
                    const incoming = res.data.result || [];
                    setOriginalData(incoming);
                    setReportList(applyExternalDateRange(applyFilters(incoming, filters)));
                } else {
                    toast.error("Rapor bulunamadı.");
                    setReportList([]);
                    setOriginalData([]);
                }
            })
            .catch((err) => {
                console.log(err);
                setReportList([]);
            })
            .finally(() => setLoading(false));
    };

    const handleSaveDetail = () => {
        const formattedData = {
            operationID: formData.operationID,
            inBranchDate: formData.inBranchDate ? formData.inBranchDate.format("YYYY-MM-DD") : null,
            barcode: formData.barcode,
            customerName: formData.customerName,
            staff: formData.staff,
            description: formData.description,
            processingType: formData.processingType,
            price: formData.price,
            paymentType: formData.paymentType,
            processingDetails: formData.processingDetails
        };

        setLoading(true);
        Services.UpdateOperationReport(parsedUser.token, [formattedData], "aras-kargo")
            .then((res) => {
                if (res.data.status) {
                    getReports();
                    handleCloseDetail();
                    toast.success("Rapor başarıyla güncellendi.");
                } else {
                    toast.error("Rapor güncellenirken hata oluştu.");
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const toggleFilterInput = (key) => {
        setShowFilterInput(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleColumnFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearColumnFilter = (key) => {
        const newFilters = { ...filters };
        delete newFilters[key];
        setFilters(newFilters);
        setShowFilterInput(prev => ({ ...prev, [key]: false }));
    };

    const clearAllFilters = () => {
        setFilters({});
        setReportList(originalData);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDetail = (row) => {
        setFormData({
            ...row,
            inBranchDate: row.inBranchDate ? moment(row.inBranchDate * 1000) : null
        });
        setOpenDetail(true);
    };
    
    const handleCloseDetail = () => setOpenDetail(false);
    
    const handleFormChange = (field, value) => setFormData({ ...formData, [field]: value });
    
    const isDetailFormValid = () => {
        if (!formData) return false;
        return !!formData.barcode && !!formData.customerName; 
    };
    
    const handleCloseModal = () => setFilterModal(false);

    const renderHeaderCell = (column) => {
        if (column.disableFilter) {
            return (
                <TableCell key={column.id} sx={{ backgroundColor: '#f4f6f8', color: '#637381', fontWeight: '600' }}>
                    {column.label}
                </TableCell>
            );
        }

        const isFilterOpen = showFilterInput[column.id];
        const currentValue = filters[column.id];
        const displayValue = typeof currentValue === 'string' ? currentValue : '';

        return (
            <TableCell 
                key={column.id} 
                sx={{ 
                    backgroundColor: '#f4f6f8', 
                    color: '#637381', 
                    fontWeight: '600',
                    minWidth: 150,
                    py: 1
                }}
            >
                {isFilterOpen ? (
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField 
                            variant="standard"
                            placeholder="Ara..."
                            value={displayValue}
                            onChange={(e) => handleColumnFilterChange(column.id, e.target.value)}
                            autoFocus
                            size="small"
                            InputProps={{
                                disableUnderline: false,
                                style: { fontSize: 13 }
                            }}
                            sx={{ bgcolor: 'white', borderRadius: 0.5, p: 0.5, width: '100%', borderWidth: 1, borderColor: '#00000036', borderStyle: 'solid' }}
                        />
                        <IconButton size="small" onClick={() => clearColumnFilter(column.id)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="space-between" 
                        gap={1} 
                        onClick={() => toggleFilterInput(column.id)} 
                        sx={{ cursor: 'pointer', p:1, '&:hover .search-icon': { opacity: 1 } }}
                    >
                        {column.label}
                        <SearchIcon 
                            className="search-icon" 
                            fontSize="small" 
                            sx={{ opacity: displayValue ? 1 : 0.3, width: 18 }} 
                        />
                    </Box>
                )}
            </TableCell>
        );
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                {tabs}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <FilterViewButton variant="contained" onClick={() => setFilterModal(true)} />
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={reportList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Sayfa başına satır:"
                    sx={{ border: 'none' }}
                />
            </Stack>

            <CustomFilter modalType="drawer" filterModal={filterModal} handleCloseModal={handleCloseModal}>
                <Stack direction="column" paddingY={2} paddingX={1} spacing={2}>
                    {TABLE_HEAD.map((column) => {
                        if (column.id === "inBranchDate") {
                            return (
                                <Stack key={column.id} direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label={`${column.label} Başlangıç`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.start || null}
                                            onChange={(newValue) => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    [column.id]: { ...(prev[column.id] || {}), start: newValue }
                                                }));
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        <DatePicker
                                            label={`${column.label} Bitiş`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.end || null}
                                            onChange={(newValue) => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    [column.id]: { ...(prev[column.id] || {}), end: newValue }
                                                }));
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            );
                        }
                        else if (column.id === "poiTitle") {
                            const options = uniqueValues(column.id);
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id={`${column.id}-label`}>{column.label}</InputLabel>
                                    <Select
                                        labelId={`${column.id}-label`}
                                        multiple
                                        input={<OutlinedInput label={column.label} />}
                                        renderValue={(selected) => (selected || []).join(", ")}
                                        value={filters[column.id] || []}
                                        onChange={(e) => {
                                            const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                            setFilters(prev => ({ ...prev, [column.id]: value }));
                                        }}
                                    >
                                        {options.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={(filters[column.id] || []).indexOf(opt) > -1} />
                                                <ListItemText primary={opt} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        else if (column.id !== 'action' && !column.disableFilter) {
                            return (
                                <TextField
                                    key={column.id}
                                    label={column.label}
                                    value={filters[column.id] || ''}
                                    onChange={(e) => setFilters(prev => ({ ...prev, [column.id]: e.target.value }))}
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                            );
                        }
                        return null;
                    })}
                </Stack>
                
                <Divider />
                <Stack direction="row" justifyContent="center" padding={2}>
                    <ClearButton onClick={clearAllFilters} />
                    <FilterButton onClick={handleCloseModal} /> 
                </Stack>
            </CustomFilter>

            <Card sx={{ boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)' }}>
                {loading ? (
                    <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
                         <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} elevation={0}>
                            <Table sx={{ minWidth: 800 }}>
                                <TableHead>
                                    <TableRow>
                                        {TABLE_HEAD.map((column) => renderHeaderCell(column))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={index} hover>
                                                {TABLE_HEAD.map((column) => {
                                                    const value = row[column.id];
                                                    return (
                                                        <TableCell key={column.id} align={column.alignRight ? 'right' : 'left'}>
                                                            {column.format ? column.format(value, row) : value}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                    ))}
                                    {reportList.length === 0 && (
                                        <TableRow>
                                            <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                                                Veri Bulunamadı
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={reportList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Sayfa başına satır:"
                        />
                    </>
                )}
            </Card>

            <Dialog open={openDetail} onClose={handleCloseDetail} fullWidth maxWidth="sm">
                <DialogTitle>Detay Düzenle</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Kargo Geliş Tarihi"
                                format="DD/MM/YYYY"
                                value={formData.inBranchDate || null}
                                onChange={(newValue) => handleFormChange('inBranchDate', newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>

                        <TextField
                            label="Gönderi No"
                            value={formData.barcode || ''}
                            onChange={(e) => handleFormChange('barcode', e.target.value.toLocaleUpperCase("tr-TR"))}
                            fullWidth
                        />

                        <TextField
                            label="Müşteri Ad Soyad"
                            value={formData.customerName || ''}
                            onChange={(e) => handleFormChange('customerName', e.target.value.toLocaleUpperCase("tr-TR"))}
                            fullWidth
                        />

                        <TextField
                            label="EP Personel Ad Soyadı"
                            value={formData.staff || ''}
                            onChange={(e) => handleFormChange('staff', e.target.value.toLocaleUpperCase("tr-TR"))}
                            fullWidth
                        />

                        <TextField
                            label="Açıklama"
                            value={formData.description || ''}
                            onChange={(e) => handleFormChange('description', e.target.value.toLocaleUpperCase("tr-TR"))}
                            fullWidth
                        />

                        <FormControl fullWidth>
                            <InputLabel id="processingType-detail-label">İşlem Türü</InputLabel>
                            <Select
                                labelId="processingType-detail-label"
                                value={formData.processingType || ''}
                                label="İşlem Türü"
                                onChange={(e) => handleFormChange('processingType', e.target.value)}
                            >
                                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                {['İade', 'Satış', 'İptal'].map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Tutar"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            value={formData.price || ''}
                            onChange={(e) => handleFormChange('price', e.target.value)}
                            fullWidth
                        />

                        <FormControl fullWidth>
                            <InputLabel id="paymentType-detail-label">Ödeme Türü</InputLabel>
                            <Select
                                labelId="paymentType-detail-label"
                                value={formData.paymentType || ''}
                                label="Ödeme Türü"
                                onChange={(e) => handleFormChange('paymentType', e.target.value)}
                            >
                                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                {["KREDİ KARTI", "NAKİT", "KARŞI ÖDEMELİ","İADE"].map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="processingDetail-detail-label">İşlem Detay</InputLabel>
                            <Select
                                labelId="processingDetail-detail-label"
                                value={formData.processingDetails || ''}
                                label="İşlem Detay"
                                onChange={(e) => handleFormChange('processingDetails', e.target.value)}
                            >
                                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                {["ANLAŞMALI", "ÜCRETLİ GÖNDERİ", "İADE"].map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>İptal</Button>
                    <Button onClick={handleSaveDetail} variant="contained" disabled={!isDetailFormValid()}>
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
